import { motion } from 'framer-motion'
import { Quote } from 'lucide-react'
import { CommunityJoinStatsView } from '../components/landing/CommunityJoinStats'
import { ParticipantsOriginMap } from '../components/landing/ParticipantsOriginMap'
import { RecentMembersMarquee } from '../components/landing/RecentMembersMarquee'
import { useCommunitySocialProofLive } from '../hooks/useCommunitySocialProofLive'
import { usePublicSocialProofStats } from '../hooks/usePublicSocialProofStats'
import { JoinCtaButton } from '../components/JoinCtaButton'
import { useT } from '../i18n/t'
import { fadeUp } from '../lib/motion'

function TestimonialCard({
  quoteKey,
  authorKey,
  metaKey,
}: {
  quoteKey: string
  authorKey: string
  metaKey: string
}) {
  const { t } = useT()

  return (
    <figure className="flex h-full flex-col pds-surface bg-paper p-6  md:p-7">
      <Quote className="h-8 w-8 text-brand/80" aria-hidden />
      <blockquote className="mt-4 flex-1 text-[15px] font-medium leading-relaxed text-ink/80 md:text-base">
        „{t(quoteKey)}"
      </blockquote>
      <figcaption className="mt-5 border-t border-ink/10 pt-4 text-sm">
        <span className="font-extrabold text-ink">{t(authorKey)}</span>
        <span className="text-ink/50"> · {t(metaKey)}</span>
      </figcaption>
    </figure>
  )
}

export function SocialProofSection() {
  const { t } = useT()
  const stats = usePublicSocialProofStats()
  const live = useCommunitySocialProofLive()

  const showRecent =
    live.status === 'ready' && live.recentMembersAvailable && live.recentMembers.length > 0
  const showMap = live.status === 'ready'

  return (
    <section
      id="spolecznosc-social-proof"
      aria-labelledby="social-proof-heading"
      className="border-b border-ink/10 bg-paper"
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        className="container-x py-12 md:py-16"
      >
        <motion.div className="content">
          <motion.p
            variants={fadeUp}
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/55"
          >
            {t('landing.socialProof.eyebrow')}
          </motion.p>

          <motion.h2
            id="social-proof-heading"
            variants={fadeUp}
            className="display mt-3 max-w-[22ch] text-[26px] font-bold leading-[1.12] text-ink md:max-w-none md:text-[40px]"
          >
            <CommunityJoinStatsView variant="headline" stats={stats} />
          </motion.h2>

          <motion.div variants={fadeUp} className="mt-5">
            <CommunityJoinStatsView variant="pills" stats={stats} />
          </motion.div>

          {live.status === 'loading' ? (
            <motion.p
              variants={fadeUp}
              className="mt-8 flex items-center gap-2 text-sm font-semibold text-ink/55 md:mt-10"
            >
              {t('landing.socialProof.recent.loading')}
            </motion.p>
          ) : showRecent ? (
            <motion.div variants={fadeUp} className="mt-8 md:mt-10">
              <RecentMembersMarquee members={live.recentMembers} />
            </motion.div>
          ) : live.status === 'ready' && !live.recentMembersAvailable ? (
            <motion.p variants={fadeUp} className="mt-8 text-sm font-semibold text-ink/55 md:mt-10">
              {t('landing.socialProof.recent.unavailable')}
            </motion.p>
          ) : null}

          {showMap ? (
            <motion.div variants={fadeUp} className="mt-8 md:mt-10">
              <ParticipantsOriginMap points={live.mapPoints} />
            </motion.div>
          ) : null}

          <motion.div
            variants={fadeUp}
            className="mt-10 grid grid-cols-1 gap-5 md:mt-12 md:grid-cols-2 md:gap-6"
          >
            <TestimonialCard
              quoteKey="landing.socialProof.testimonial1.text"
              authorKey="landing.socialProof.testimonial1.author"
              metaKey="landing.socialProof.testimonial1.meta"
            />
            <TestimonialCard
              quoteKey="landing.socialProof.testimonial2.text"
              authorKey="landing.socialProof.testimonial2.author"
              metaKey="landing.socialProof.testimonial2.meta"
            />
          </motion.div>

          <motion.div variants={fadeUp} className="mt-10 flex justify-center md:mt-12">
            <JoinCtaButton to="/spolecznosc" size="lg">
              {t('landing.socialProof.cta')}
            </JoinCtaButton>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  )
}
